import { BoxProps, ColorOptions } from '@jfs/ui-new'
import { useFocusEffect } from '@react-navigation/native'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useWindowDimensions } from 'react-native'
import { Container } from '../../components/Container'
import { NewTable } from '../../components/NewTable'
import { useLazyApiData, useStores } from '../../data/store'
import { dateFormatter, DateFormatTypes } from '../../utils'
import { FiltersModal } from '../EntityOnboarding/OnboardEntity/comps/FiltersModal'
import { FullScreenLoader } from '../EntityOnboarding/OnboardEntity/comps/FullScreenLoader'
import { MakerCheckerHeader } from './MakerCheckerHeader'
import { getCustomColumnWidth, getDefaultFilterData, getMakerCheckerData } from './makerCheckerProps'

interface GenericMakerCheckerScreenProps {
  headerTitle: string
  getListApi: any
  exportJobType: string
  headerRowKeys: any
  filterData?: any
  enableFilter: boolean
  showCalendar: boolean
  showRefresh: boolean
}

const GenericMakerCheckerScreen = ({ SCREEN_TYPE }) => {
  const [filterVisible, setFilterVisible] = useState(false)

  const { exportByJobIdAsync, getGenericMakerCheckerList, selectedDateInfo, getAllEntities, resetPagination } =
    useStores((store) => ({
      exportByJobIdAsync: store.exportByJobIdAsync,
      getGenericMakerCheckerList: store.getGenericMakerCheckerList,
      selectedDateInfo: store.selectedDateInfo,
      getAllEntities: store.entityMaster.getAllEntities,
      resetPagination: store.resetPagination,
    }))

  const [lazyGetMakerCheckerList, getMakerCheckerListHandler] = useLazyApiData(getGenericMakerCheckerList)
  const [lazyDownloadExistingRecords, downloadExistingRecordsHandler] = useLazyApiData(exportByJobIdAsync)
  const [getAllEntitiesApi, getAllEntitiesHandler] = useLazyApiData(getAllEntities)

  const entityIdList = getAllEntitiesHandler?.data?.content?.map((item) => ({
    ...item,
    value: item?.id + '-' + item?.name,
  }))

  const props: GenericMakerCheckerScreenProps = getMakerCheckerData?.[SCREEN_TYPE]?.(entityIdList)
  const [selectedFilterData, setSelectedFilterData] = useState(getDefaultFilterData(props?.filterData))

  const abortControllerRef: any = useRef({})

  const getAbortController = useCallback(() => {
    abortControllerRef.current.abortController = new AbortController()
    return abortControllerRef.current
  }, [])

  useEffect(
    () => () => {
      if (abortControllerRef && abortControllerRef.current) {
        abortControllerRef.current.abortRecursive = true
      }
    },
    [],
  )

  const fetchEntityList = useCallback(() => {
    if (SCREEN_TYPE === 'BATCH_MAKER') {
      getAllEntitiesApi({}, { page: 1, size: 10000 }, true)
    }
  }, [SCREEN_TYPE, getAllEntitiesApi])

  useEffect(() => {
    fetchEntityList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDownloadExistingRecordPress = useCallback(
    (reqBody) => {
      lazyDownloadExistingRecords(reqBody, getAbortController)
    },
    [getAbortController, lazyDownloadExistingRecords],
  )

  const NON_ARRAY_FILTERS = ['processingEntity', 'mid']

  const parseFilterData: any = useCallback((filterData) => {
    console.log('htag filterData', filterData)
    const parsedFilterData: any = Object.keys(filterData).reduce(
      (acc, item) => ({
        ...acc,
        ...(!NON_ARRAY_FILTERS.includes(item) && { [item]: filterData?.[item]?.map((data) => data?.key) }),
        ...(NON_ARRAY_FILTERS.includes(item) && { [item]: filterData?.[item] }),
      }),
      {},
    )
    if (parsedFilterData.processingEntity) {
      parsedFilterData.processingEntity = parsedFilterData.processingEntity.split('-')[0]
    }
    console.log('parseFilterData', parsedFilterData)
    return parsedFilterData
  }, [])

  const fetchDataFromServer = useCallback(() => {
    lazyGetMakerCheckerList(props?.getListApi, parseFilterData(selectedFilterData))
  }, [lazyGetMakerCheckerList, parseFilterData, props?.getListApi, selectedFilterData])

  useFocusEffect(useCallback(() => fetchDataFromServer(), [fetchDataFromServer]))

  const onApplyFiter = useCallback((selectedFilter: any) => {
    resetPagination()
    setSelectedFilterData(selectedFilter)
  }, [])

  const downloadBatchRecord = useCallback(
    (...args) => {
      console.log(args)
      onDownloadExistingRecordPress({
        jobType: 'BATCH_TRANSACTION_EXPORT',
        batchClosureFilter: {
          merchantId: args[2]?.mid,
          terminalId: args[2]?.tid,
          batchNumber: args[2]?.batchNo,
          processingEntity: args[2]?.processingEntityId,
        },
      })
    },
    [onDownloadExistingRecordPress],
  )
  const { width } = useWindowDimensions()

  const exportAllBatchData = () => {
    onDownloadExistingRecordPress({
      jobType: props?.exportJobType,
      fromDate: dateFormatter(selectedDateInfo?.fromDate + '', DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE),
      toDate: dateFormatter(selectedDateInfo?.toDate + '', DateFormatTypes.DATE_ONLY_WITHOUT_TIME_REVERSE),
      ...(SCREEN_TYPE === 'BATCH_MAKER' && (selectedFilterData?.mid || selectedFilterData?.processingEntity)
        ? {
            batchClosureFilter: {
              merchantId: selectedFilterData?.mid,
              processingEntity: selectedFilterData?.processingEntity.split('-')[0],
            },
          }
        : {}),
    })
  }

  return (
    <Container
      {...styles.container}
      isLoading={getAllEntitiesHandler.state === 'pending'}
      errorMessage={getAllEntitiesHandler?.data?.message}
      onRetry={fetchEntityList}>
      <MakerCheckerHeader
        title={props?.headerTitle}
        fetchDataOnChange={fetchDataFromServer}
        onRefresh={fetchDataFromServer}
        filterVisible={filterVisible}
        setFilterVisible={setFilterVisible}
        exportRecords={exportAllBatchData}
        enableFilter={props?.enableFilter}
        showCalendar={props?.showCalendar}
        showRefresh={props?.showRefresh}
      />
      <Container
        isLoading={getMakerCheckerListHandler?.state === 'pending'}
        errorMessage={getMakerCheckerListHandler?.data?.message}
        emptyMessage={
          getMakerCheckerListHandler?.state === 'fulfilled' &&
          getMakerCheckerListHandler?.data?.length === 0 &&
          'No Data Found'
        }
        flex={1}
        onRetry={fetchDataFromServer}>
        <NewTable
          data={getMakerCheckerListHandler?.data ?? []}
          rowHeight={48}
          headerHeight={64}
          customWidthFunc={getCustomColumnWidth}
          tableWidth={Math.max(Object.keys(props?.headerRowKeys).length * 150, width)}
          headerRowKeys={props?.headerRowKeys}
          onRowClick={() => {}}
          onRetry={() => {}}
          onAction={downloadBatchRecord}
        />
      </Container>
      <FiltersModal
        show={filterVisible}
        onDismiss={() => setFilterVisible(false)}
        initialValue={selectedFilterData}
        data={props?.filterData ?? []}
        onApplyFiter={onApplyFiter}
        onReset={() => onApplyFiter(getDefaultFilterData(props?.filterData))}
        entityList={getAllEntitiesHandler?.data?.content}
      />
      <FullScreenLoader show={downloadExistingRecordsHandler?.state === 'pending'} />
    </Container>
  )
}
export default GenericMakerCheckerScreen
const styles = {
  container: {
    h: '100%',
    borderRadius: 8,
    shadowRadius: 8,
    shadowColor: '#0000001F' as ColorOptions,
  } as BoxProps,
  innerContainer: {
    flexDirection: 'row',
    flex: 1,
  } as BoxProps,
}
